import I18n from "i18next";

import { useInternationalFormatters } from "../../../shared/helpers/useInternationalFormatters";

import {
  MeasurementDetail,
  TEVMeasurement,
  UHFMeasurement,
  CablePDMeasurement,
  UltraSonicMeasurement,
  VPISMeasurement,
} from "../../../shared/interfaces/Result";
import { MeasurementType, RAGStatus } from "../../../shared/enums/Result";
import {
  isTEVMeasurementDetailArray,
  isUHFMeasurementDetailArray,
  isCablePDMeasurementDetailArray,
  isUltrasonicMeasurementDetailArray,
  isVPISMeasurementDetailArray,
} from "../../../shared/helpers/ResultHelpers";

export function useMeasurementHistoryChartOptions<Measurement>(
  measurementHistory?: MeasurementDetail<Measurement>[],
  measurementType?: MeasurementType,
) {
  const { formatNumber } = useInternationalFormatters();
  function numericPointFormatter(this: Highcharts.Point) {
    const formattedYValue = formatNumber(Number(this.y), 3);

    return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formattedYValue}</b><br/>`;
  }

  if (measurementHistory && measurementHistory.length > 0) {
    if (isTEVMeasurementDetailArray(measurementHistory)) {
      return getOptionsTEV(measurementHistory, numericPointFormatter);
    } else if (isUHFMeasurementDetailArray(measurementHistory)) {
      return getOptionsUHF(measurementHistory, numericPointFormatter);
    } else if (isCablePDMeasurementDetailArray(measurementHistory)) {
      return getOptionsCablePD(measurementHistory, numericPointFormatter);
    } else if (isUltrasonicMeasurementDetailArray(measurementHistory)) {
      return getOptionsUltrasonic(measurementHistory, numericPointFormatter);
    } else if (isVPISMeasurementDetailArray(measurementHistory)) {
      return getOptionsVPIS(measurementHistory, numericPointFormatter);
    }
  }
}

interface ChartOptions<T> {
  seriesConfig: SeriesConfig<T>[];
  series: ChartSeriesOptions[];
  yAxisConfig?: Highcharts.YAxisOptions | Highcharts.YAxisOptions[];
}

type ChartSeriesOptions = Partial<Highcharts.SeriesOptionsType> & Record<string, any>;

interface SeriesConfig<T> {
  propertyKey: keyof T;
  translationKeyPrefix?: string;
  chartSeriesOptions: ChartSeriesOptions;
}

function buildSeriesData<
  T extends TEVMeasurement | UHFMeasurement | CablePDMeasurement | UltraSonicMeasurement | VPISMeasurement,
>(measurements: MeasurementDetail<T>[], seriesConfig: SeriesConfig<T>[]) {
  let chartSeriesOptions: ChartSeriesOptions[] = [];

  seriesConfig.forEach((config: any) => {
    let series = {
      ...config.chartSeriesOptions,
      data: [] as Highcharts.Point[],
    };
    measurements.forEach((measurement) => {
      const time = measurement.time && new Date(measurement.time).getTime();
      const propertyValue = measurement.measurement[config.propertyKey as keyof T];

      let numericValue: number | undefined = 0;
      let stringValue: string | undefined = "";

      if (propertyValue == null) {
        numericValue = undefined;
        stringValue = undefined;
      } else if (typeof propertyValue === "number") numericValue = propertyValue;
      else if (typeof propertyValue === "string")
        stringValue = I18n.t(`${config.translationKeyPrefix}.${propertyValue.toString()}`);

      const exists = series.data.some((point: Highcharts.Point) => point.x === time && point.y === numericValue);

      if (!exists && measurement.measurement.RAGStatus !== RAGStatus.None && numericValue != null) {
        series.data.push({ x: time, y: numericValue, name: stringValue });
      }
    });

    chartSeriesOptions.push(series);
  });

  chartSeriesOptions = chartSeriesOptions.map((o) => {
    return {
      ...o,
      data: o.data.sort(function (a: Highcharts.Point, b: Highcharts.Point) {
        return a.x - b.x;
      }),
    };
  });

  return chartSeriesOptions;
}

const stringPointFormat = '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.name}</b><br/>';

export function getOptionsTEV(
  measurementHistory: MeasurementDetail<TEVMeasurement>[],
  numericPointFormatter: Highcharts.FormatterCallbackFunction<Highcharts.Point>,
) {
  const chartOptions: ChartOptions<TEVMeasurement> = {
    seriesConfig: [
      {
        chartSeriesOptions: {
          name: I18n.t("units.db"),
          tooltip: {
            pointFormatter: numericPointFormatter,
          },
          yAxis: 0,
        },
        propertyKey: "MeasureDB",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("units.ppc"),
          tooltip: {
            pointFormatter: numericPointFormatter,
          },
          yAxis: 1,
        },
        propertyKey: "MeasurePPC",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("results.switchPosition"),
          tooltip: {
            pointFormat: stringPointFormat,
          },
        },
        propertyKey: "SwitchPosition",
        translationKeyPrefix: "UTP2.switchPosition",
      },
    ],
    series: [],
    yAxisConfig: [
      {
        title: {
          text: I18n.t("units.db"),
        },
        min: 0,
        max: 60,
        opposite: false,
      },
      {
        title: {
          text: I18n.t("units.ppc"),
        },
        opposite: true,
      },
    ],
  };

  chartOptions.series = buildSeriesData<TEVMeasurement>(measurementHistory, chartOptions.seriesConfig);
  return chartOptions;
}

export function getOptionsUHF(
  measurementHistory: MeasurementDetail<UHFMeasurement>[],
  numericPointFormatter: Highcharts.FormatterCallbackFunction<Highcharts.Point>,
) {
  const chartOptions: ChartOptions<UHFMeasurement> = {
    seriesConfig: [
      {
        chartSeriesOptions: {
          name: I18n.t("units.dbm"),
          tooltip: {
            pointFormatter: numericPointFormatter,
          },
        },
        propertyKey: "MeasureDBM",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("results.switchPosition"),
          tooltip: {
            pointFormat: stringPointFormat,
          },
        },
        propertyKey: "SwitchPosition",
        translationKeyPrefix: "UTP2.switchPosition",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("measurements.Frequency"),
          tooltip: {
            pointFormatter: numericPointFormatter,
          },
        },
        propertyKey: "Frequency",
      },
    ],
    series: [],
  };

  chartOptions.series = buildSeriesData<UHFMeasurement>(measurementHistory, chartOptions.seriesConfig);
  return chartOptions;
}

export function getOptionsCablePD(
  measurementHistory: MeasurementDetail<CablePDMeasurement>[],
  numericPointFormatter: Highcharts.FormatterCallbackFunction<Highcharts.Point>,
) {
  const chartOptions: ChartOptions<CablePDMeasurement> = {
    seriesConfig: [
      {
        chartSeriesOptions: {
          name: I18n.t("units.pc"),
          tooltip: {
            pointFormatter: numericPointFormatter,
          },
          yAxis: 0,
        },
        propertyKey: "MeasurePC",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("units.ppc"),
          tooltip: {
            pointFormatter: numericPointFormatter,
          },
          yAxis: 1,
        },
        propertyKey: "MeasurePPC",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("results.switchPosition"),
          tooltip: {
            pointFormat: stringPointFormat,
          },
        },
        propertyKey: "SwitchPosition",
        translationKeyPrefix: "UTP2.switchPosition",
      },
    ],
    series: [],
    yAxisConfig: [
      {
        title: {
          text: I18n.t("units.pc"),
        },
        opposite: false,
      },
      {
        title: {
          text: I18n.t("units.ppc"),
        },
        opposite: true,
      },
    ],
  };

  chartOptions.series = buildSeriesData<CablePDMeasurement>(measurementHistory, chartOptions.seriesConfig);
  return chartOptions;
}

export function getOptionsUltrasonic(
  measurementHistory: MeasurementDetail<UltraSonicMeasurement>[],
  numericPointFormatter: Highcharts.FormatterCallbackFunction<Highcharts.Point>,
) {
  const chartOptions: ChartOptions<UltraSonicMeasurement> = {
    seriesConfig: [
      {
        chartSeriesOptions: {
          name: I18n.t("units.dbuv"),
          tooltip: {
            pointFormatter: numericPointFormatter,
          },
        },
        propertyKey: "MeasureDBUV",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("results.switchPosition"),
          tooltip: {
            pointFormat: stringPointFormat,
          },
        },
        propertyKey: "SwitchPosition",
        translationKeyPrefix: "UTP2.switchPosition",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("measurements.UltraSonicClassification"),
          tooltip: {
            pointFormat: stringPointFormat,
          },
        },
        propertyKey: "UltraSonicClassification",
        translationKeyPrefix: "UltrasonicClassification",
      },
    ],
    series: [],
  };

  chartOptions.series = buildSeriesData<UltraSonicMeasurement>(measurementHistory, chartOptions.seriesConfig);
  return chartOptions;
}

export function getOptionsVPIS(
  measurementHistory: MeasurementDetail<VPISMeasurement>[],
  numericPointFormatter: Highcharts.FormatterCallbackFunction<Highcharts.Point>,
) {
  const chartOptions: ChartOptions<VPISMeasurement> = {
    seriesConfig: [
      {
        chartSeriesOptions: {
          name: I18n.t("units.mV"),
          tooltip: {
            pointFormatter: numericPointFormatter,
          },
          yAxis: 0,
        },
        propertyKey: "MeasureMV",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("units.ppc"),
          tooltip: {
            pointFormatter: numericPointFormatter,
          },
          yAxis: 1,
        },
        propertyKey: "MeasurePPC",
      },
      {
        chartSeriesOptions: {
          name: I18n.t("results.switchPosition"),
          tooltip: {
            pointFormat: stringPointFormat,
          },
        },
        propertyKey: "SwitchPosition",
        translationKeyPrefix: "UTP2.switchPosition",
      },
    ],
    series: [],
    yAxisConfig: [
      {
        title: {
          text: I18n.t("units.pc"),
        },
        opposite: false,
      },
      {
        title: {
          text: I18n.t("units.ppc"),
        },
        opposite: true,
      },
    ],
  };

  chartOptions.series = buildSeriesData<VPISMeasurement>(measurementHistory, chartOptions.seriesConfig);
  return chartOptions;
}
